
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { CarrierT } from '@/core/types/carriers.types'
import { UserT } from '@/core/types/users.types'
import { CityT } from '@/core/types/cities.types'

// realisations
import { carriers } from '@/core/realisations/carriers.realisation'
import { users } from '@/core/realisations/users.realisation'
import { cities } from '@/core/realisations/cities.realisation'

// constants
import { STATUSES_RPK_RPP } from '@/core/constants/orders.constants'

export default defineComponent({
    components: {
        TheInput,
        TheSelect,
        TheButton,
        Checkbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['reset', 'submit'],
    setup(props, { emit }) {
        const state = reactive({
            act_number: null as string | null,
            status_id: null as number | null,
            company_id: null as number | null,
            user_id: null as number | null,
            act_date: null as string | null,
            from_city_id: null as number | null,
            where_city_id: null as number | null,
            turnover_date_from: null as string | null,
            turnover_date_to: null as string | null,
            trip_number: null as string | null,
            ttn_number: null as string | null,
            is_above_cost_standard: null as number | null,
        })

        const data_state = reactive({
            carriers: new DataState<CarrierT>({}),
            cities: new DataState<CityT>({}),
            logists: new DataState<UserT>({}),
        })

        const onSubmit = () => {
            emit('submit', {
                act_number: state.act_number,
                status_id: state.status_id,
                company_id: state.company_id,
                user_id: state.user_id,
                act_date: state.act_date,
                from_city_id: state.from_city_id,
                where_city_id: state.where_city_id,
                turnover_date_from: state.turnover_date_from,
                turnover_date_to: state.turnover_date_to,
                trip_number: state.trip_number,
                ttn_number: state.ttn_number,
                is_above_cost_standard: state.is_above_cost_standard,
            })
        }

        const reset = () => {
            state.act_number = null
            state.status_id = null
            state.company_id = null
            state.user_id = null
            state.act_date = null
            state.from_city_id = null
            state.where_city_id = null
            state.turnover_date_from = null
            state.turnover_date_to = null
            state.trip_number = null
            state.ttn_number = null
            state.is_above_cost_standard = null

            emit('reset')
        }

        const loadCompanies = async (page: number, search?: string) => {
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true
                const response = await carriers.getList({ page, search, statusId: 2 })
                data_state.carriers.list = response.data

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.carriers.pagination.total_pages) return
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true

                const response = await carriers.getList({ page, statusId: 2 })
                data_state.carriers.list = data_state.carriers.list.concat(response.data)

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CarrierT) => {
            await loadCompanies(data_state.carriers.pagination.current_page)
            data_state.carriers.list.unshift(company)
        }

        const loadCities = async (page: number, search?: string) => {
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const data = await cities.getAllList(page, search, 9999)
                data_state.cities.list = data.data

                data_state.cities.pagination.total_pages = data.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const loadLogists = async (page: number, name?: string) => {
            data_state.logists.pagination.current_page = page

            try {
                data_state.logists.is_loading = true

                const logists = await users.getLogists(page, name, 0)

                data_state.logists.list = logists.data

                data_state.logists.pagination.total_pages = logists.meta.last_page

                data_state.logists.is_failed = false
            } catch (error) {
                data_state.logists.is_failed = true
                console.error(error)
            } finally {
                data_state.logists.is_loading = false
            }
        }

        const loadMoreLogists = async (page: number) => {
            if (page > data_state.logists.pagination.total_pages) return
            data_state.logists.pagination.current_page = page

            try {
                data_state.logists.is_loading = true

                const logists = await users.getLogists(page, undefined, 0)
                data_state.logists.list = data_state.logists.list.concat(logists.data)

                data_state.logists.pagination.total_pages = logists.meta.last_page

                data_state.logists.is_failed = false
            } catch (error) {
                data_state.logists.is_failed = true
                console.error(error)
            } finally {
                data_state.logists.is_loading = false
            }
        }

        const searchLogists = async (search_query: string) => {
            loadLogists(1, search_query)
        }

        const unshiftFindedLogist = async (logist: UserT) => {
            await loadLogists(data_state.logists.pagination.current_page)
            data_state.logists.list.unshift(logist)
        }

        onBeforeMount(() => {
            loadCities(1)
            loadCompanies(1)
            loadLogists(1)
        })

        return {
            state,
            STATUSES_RPK_RPP,
            data_state,
            onSubmit,
            reset,
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            loadCities,
            loadLogists,
            loadMoreLogists,
            searchLogists,
            unshiftFindedLogist,
        }
    },
})
